<script setup lang="ts">
import { computed, inject } from "vue";

import Editor from "@tinymce/tinymce-vue";
import { ElMessage } from "element-plus";

import { formActivityInjectKey } from "../Renderer/formActivity";
import { maxPastedImageBytes } from "./discussionRichText";

const props = defineProps<{
  disabled?: boolean;
  placeholder?: string | null;
}>();

const modelValue = defineModel<string>({ default: "" });

// TinyMCE renders its editable content inside an <iframe>, so keystrokes never
// reach the form renderer's .formHost capture handler that keeps the idle timer
// alive. Report activity directly from the editor's own key events so the form
// doesn't flip to read-only while the user is typing a comment.
const reportActivity = inject(formActivityInjectKey, () => {});

interface TinyEditor {
  on: (events: string, handler: (event: unknown) => void) => void;
}

const wireActivityReporting = (editor: TinyEditor) => {
  editor.on("keydown keyup input paste", () => reportActivity());
};

/**
 * Pasted images are embedded as base64 in the comment itself, so an oversized
 * paste would be carried in the answer JSON on every load of the form response.
 * Refuse it up front — silently truncating or letting a multi-megabyte image
 * through would both be worse than telling the user no.
 */
const guardImageSize = (editor: TinyEditor) => {
  editor.on("paste", (event) => {
    const files = (event as ClipboardEvent).clipboardData?.files;
    if (!files?.length) {
      return;
    }

    const tooBig = Array.from(files).find(
      (file) =>
        file.type.startsWith("image/") && file.size > maxPastedImageBytes,
    );

    if (tooBig) {
      (event as ClipboardEvent).preventDefault();
      ElMessage({
        message: `Images must be under ${Math.round(
          maxPastedImageBytes / (1024 * 1024),
        )}MB`,
        type: "warning",
      });
    }
  });
};

const setupEditor = (editor: TinyEditor) => {
  wireActivityReporting(editor);
  guardImageSize(editor);
};

// Matches RichText.vue: the iframe can't inherit the page's fonts and colors,
// so the intake side has to load Poppins into the iframe itself for comments to
// read like the rest of the form. "default" keeps TinyMCE's standard content
// stylesheet alongside it.
const iframeContentOptions = () =>
  document.body.classList.contains("intake")
    ? {
        content_css: [
          "default",
          "https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,400;0,700;1,400;1,700&display=swap",
        ],
        content_style:
          "body { font-family: Poppins, sans-serif; font-size: 1rem; color: #2a2829; }",
      }
    : {
        content_style: "body { font-size: 1rem; color: #333; }",
      };

// A deliberately small toolbar — comments are short prose, not form content, so
// the block/font/table controls the RichText element exposes would be noise.
const tinyMCEOptions = computed(() => ({
  promotion: false,
  license_key: "gpl",
  plugins: "lists link autolink image",
  toolbar:
    "bold italic underline | bullist numlist | link image | removeformat",
  // Pasted images embed as base64 rather than uploading; guardImageSize caps
  // how large that can get.
  paste_data_images: true,
  // No upload endpoint exists for comments, so keep TinyMCE from trying to POST
  // the blob somewhere and blanking the image when that fails.
  automatic_uploads: false,
  // The URL field alone is enough — alt/dimensions/caption are noise here.
  image_description: false,
  image_dimensions: false,
  toolbar_mode: "wrap",
  menubar: false,
  statusbar: false,
  resize: true,
  // Comments are short — a couple of lines is the norm — so this comes in
  // deliberately shorter than the RichText element's ~200px default. `resize`
  // stays on so anyone writing a long comment can drag it taller.
  height: 120,
  placeholder: props.placeholder ?? "",
  setup: setupEditor,
  ...iframeContentOptions(),
}));
</script>

<template>
  <div class="DiscussionThreadEditor">
    <Editor
      v-model="modelValue"
      :init="tinyMCEOptions"
      license-key="gpl"
      :disabled="disabled"
    />
  </div>
</template>

<style lang="less">
// The Editor component renders only a <textarea>; TinyMCE then inserts its
// .tox-tinymce shell as a sibling of that textarea. Since that shell is created
// outside this template, a scoped rule's data attribute never lands on it —
// hence the plain wrapper class rather than `scoped`.
.DiscussionThreadEditor {
  // min-width guards against the flex/grid `auto` minimum, which otherwise lets
  // the toolbar's intrinsic width collapse the editor to a narrow column.
  min-width: 0;
  width: 100%;

  .tox-tinymce {
    width: 100%;
    max-width: 100%;
  }
}
</style>
